'use client';

import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { AppLink } from '@/components/mui/app-link';
import { AppLinkButton } from '@/components/mui/app-link-button';
import type { InstrumentRow } from '@/components/catalogo/types';
import { displayInstrument } from '@/libs/normalize-ticker';
import type { CatalogDefaultSearch } from '@/libs/catalogo-default-searches';
import { encodeCatalogoVisibleColumnsBitset } from '@/libs/catalogo-compact-state';
import { useAppColors } from '@/styles/colors';

export type StrategyPreviewState = {
  loading: boolean;
  error: string | null;
  rows: InstrumentRow[];
  total: number;
};

const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});

const RESULT_CHIP_SX = {
  borderRadius: 999,
  fontWeight: 800,
  height: 30,
  fontSize: '0.78rem',
  px: 0.8,
};

const PRIMARY_ACTION_BUTTON_SX = {
  textTransform: 'none',
  fontWeight: 800,
  px: 1.8,
  minHeight: 38,
  borderRadius: 2,
};

const SECONDARY_ACTION_BUTTON_SX = {
  textTransform: 'none',
  fontWeight: 800,
  px: 1.8,
  minHeight: 38,
  borderRadius: 2,
};

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${PERCENT_FORMATTER.format(value)}%`;
};

const formatPvp = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatInteger = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return INTEGER_FORMATTER.format(value);
};

const IDEAS_BASE_COLUMNS = ['idCem', 'name', 'dy12mPct', 'currentPvp'];
const IDEAS_TARGET_COLUMN_COUNT = 7;
const IDEAS_FALLBACK_COLUMNS = [
  'liquidezDiaria30d',
  'numeroCotistas',
  'patrimonioLiquido',
  'vacanciaPct',
  'inadimplenciaPct',
  'distanceTo12mHighPct',
  'selicSensitivityScore',
  'leverageIndexScore',
  'concentracaoTijoloPct',
  'concentracaoPapelPct',
  'classificacao',
  'segmento',
  'cashTrendStatus',
];
const SORT_FIELD_TO_COLUMN: Record<string, string> = {
  dy12mPct: 'dy12mPct',
  liquidezDiaria30d: 'liquidezDiaria30d',
  patrimonioLiquido: 'patrimonioLiquido',
  currentPvp: 'currentPvp',
  vpPerShareGrowth12mPct: 'vpPerShareGrowth12mPct',
  distanceTo12mHighPct: 'distanceTo12mHighPct',
  selicSensitivityScore: 'selicSensitivityScore',
  leverageIndexScore: 'leverageIndexScore',
  vacanciaPct: 'vacanciaPct',
  inadimplenciaPct: 'inadimplenciaPct',
  concentracaoTijoloPct: 'concentracaoTijoloPct',
  concentracaoPapelPct: 'concentracaoPapelPct',
};
const FILTER_KEY_TO_COLUMN: Record<string, string> = {
  classificacao: 'classificacao',
  segmento: 'segmento',
  cashTrendStatus: 'cashTrendStatus',
  vacanciaMin: 'vacanciaPct',
  vacanciaMax: 'vacanciaPct',
  inadimplenciaMin: 'inadimplenciaPct',
  inadimplenciaMax: 'inadimplenciaPct',
  liquidezDiaria30dMin: 'liquidezDiaria30d',
  liquidezDiaria30dMax: 'liquidezDiaria30d',
  cotistasMin: 'numeroCotistas',
  cotistasMax: 'numeroCotistas',
  patrimonioMin: 'patrimonioLiquido',
  patrimonioMax: 'patrimonioLiquido',
  distanceTo12mHighMin: 'distanceTo12mHighPct',
  distanceTo12mHighMax: 'distanceTo12mHighPct',
  selicSensitivityScoreMin: 'selicSensitivityScore',
  selicSensitivityScoreMax: 'selicSensitivityScore',
  leverageIndexScoreMin: 'leverageIndexScore',
  leverageIndexScoreMax: 'leverageIndexScore',
  concentracaoTijoloMin: 'concentracaoTijoloPct',
  concentracaoTijoloMax: 'concentracaoTijoloPct',
  concentracaoPapelMin: 'concentracaoPapelPct',
  concentracaoPapelMax: 'concentracaoPapelPct',
  vpPerShareGrowth12mMin: 'vpPerShareGrowth12mPct',
  vpPerShareGrowth12mMax: 'vpPerShareGrowth12mPct',
};

const buildIdeasVisibleColumns = (queryString: string): string[] => {
  const params = new URLSearchParams(queryString);
  const out: string[] = [...IDEAS_BASE_COLUMNS];
  const has = (field: string) => out.includes(field);
  const tryAdd = (field: string | null | undefined) => {
    if (!field || has(field)) return;
    out.push(field);
  };

  const sortRaw = (params.get('sort') ?? '').trim();
  if (sortRaw) {
    sortRaw
      .split(',')
      .map((chunk) => (chunk.split(':')[0] ?? '').trim())
      .filter(Boolean)
      .forEach((field) => tryAdd(SORT_FIELD_TO_COLUMN[field] ?? null));
  }

  params.forEach((_, key) => {
    tryAdd(FILTER_KEY_TO_COLUMN[key] ?? null);
  });

  IDEAS_FALLBACK_COLUMNS.forEach((field) => {
    if (out.length >= IDEAS_TARGET_COLUMN_COUNT) return;
    tryAdd(field);
  });

  return out.slice(0, IDEAS_TARGET_COLUMN_COUNT);
};

export const buildIdeasCatalogUrl = (catalogPath: string, queryString: string, strategyId: string) => {
  const params = new URLSearchParams(queryString);
  params.set('strategy', strategyId);
  params.set('c', encodeCatalogoVisibleColumnsBitset(buildIdeasVisibleColumns(queryString)));
  const nextQuery = params.toString();
  return nextQuery ? `${catalogPath}?${nextQuery}` : catalogPath;
};

type StrategyCardProps = {
  strategy: CatalogDefaultSearch;
  state: StrategyPreviewState;
  avoidWhen: string;
  onOpenCriteria: (strategy: CatalogDefaultSearch) => void;
};

export function StrategyCard({ strategy, state, avoidWhen, onOpenCriteria }: StrategyCardProps) {
  const appColors = useAppColors();
  const catalogUrl = buildIdeasCatalogUrl(strategy.catalogPath, strategy.queryString, strategy.id);

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: 'hsl(var(--border))',
        backgroundColor: 'hsl(var(--card))',
        transition: 'border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          borderColor: appColors.actions.accentOutline.hoverBorder,
          boxShadow: '0 8px 24px hsl(var(--foreground) / 0.08)',
        },
      }}
    >
      <Stack spacing={1.1} sx={{ height: '100%' }}>
        <Stack spacing={0.4}>
          <Typography variant='subtitle1' fontWeight={800}>
            {strategy.name}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ minWidth: 0 }}>
            {strategy.description}
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: 'hsl(var(--muted-foreground))',
              fontWeight: 700,
              display: 'block',
            }}
          >
            Para quem: {strategy.forWhom}
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: 'hsl(var(--muted-foreground))',
              display: 'block',
            }}
          >
            Quando evitar: {avoidWhen}
          </Typography>
        </Stack>

        <Divider />

        {state.loading ? (
          <Stack direction='row' spacing={0.8} alignItems='center'>
            <CircularProgress size={14} />
            <Typography variant='caption' color='text.secondary'>
              Carregando prévia dos resultados...
            </Typography>
          </Stack>
        ) : state.error ? (
          <Alert severity='warning' sx={{ py: 0.4 }}>
            {state.error}
          </Alert>
        ) : state.rows.length === 0 ? (
          <Typography variant='caption' color='text.secondary'>
            Nenhum resultado na prévia para essa estratégia.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 0.25,
              p: 0.95,
              border: '1px solid hsl(var(--border))',
              borderRadius: 1.5,
              bgcolor: 'hsl(var(--background) / 0.55)',
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 0.2 }}>
              <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                Top 5 prévia
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', ml: 1 }}
              >
                DY 12m ↓
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '70px minmax(56px, 1fr) minmax(52px, 1fr)',
                  sm: '72px minmax(58px, 1fr) minmax(52px, 1fr) minmax(58px, 1fr)',
                },
                alignItems: 'center',
                columnGap: 0.8,
                px: 0.6,
                py: 0.35,
                borderRadius: 1,
                bgcolor: 'hsl(var(--muted) / 0.45)',
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'left' }}>
                Instrument
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>
                DY 12m
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>
                P/VP
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  textAlign: 'right',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Cot.
              </Typography>
            </Box>
            {state.rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '70px minmax(56px, 1fr) minmax(52px, 1fr)',
                    sm: '72px minmax(58px, 1fr) minmax(52px, 1fr) minmax(58px, 1fr)',
                  },
                  alignItems: 'center',
                  columnGap: 0.8,
                  px: 0.6,
                  py: 0.22,
                  borderRadius: 1,
                  '&:nth-of-type(even)': { bgcolor: 'hsl(var(--muted) / 0.2)' },
                }}
              >
                {row.idCem ? (
                  <AppLink
                    href={`/ativo/${encodeURIComponent(displayInstrument(row.idCem, row.fundTypeSymbol))}`}
                    underline='hover'
                    variant='caption'
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.74rem',
                      color: appColors.actions.accentOutline.text,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayInstrument(row.idCem, row.fundTypeSymbol)}
                  </AppLink>
                ) : (
                  <Typography
                    variant='caption'
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.74rem',
                      color: appColors.actions.accentOutline.text,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    -
                  </Typography>
                )}
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatPercent(row.dy12mPct)}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatPvp(row.currentPvp)}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', display: { xs: 'none', sm: 'block' } }}
                >
                  {formatInteger(row.numeroCotistas)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 'auto' }}>
          <Chip
            size='small'
            label={`${formatInteger(state.total)} resultados`}
            variant='outlined'
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              ...RESULT_CHIP_SX,
              color: appColors.actions.range.unselectedText,
              borderColor: appColors.actions.range.unselectedBorder,
            }}
          />
          <AppLinkButton
            href={catalogUrl}
            variant='contained'
            color='warning'
            size='small'
            sx={{
              ...PRIMARY_ACTION_BUTTON_SX,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Abrir estratégia
          </AppLinkButton>
          <Button
            variant='outlined'
            size='small'
            onClick={() => onOpenCriteria(strategy)}
            sx={{
              ...SECONDARY_ACTION_BUTTON_SX,
              color: appColors.actions.accentOutline.text,
              borderColor: appColors.actions.accentOutline.border,
              '&:hover': {
                color: appColors.actions.accentOutline.hoverText,
                borderColor: appColors.actions.accentOutline.hoverBorder,
                backgroundColor: appColors.actions.accentOutline.hoverBg,
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Ver critérios
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
